/**
 * Separator — séparateur Bézier dessiné à la main (style Excalidraw).
 *
 * @remarks
 * Deux passes Bézier indépendantes générées au mount via `useMemo` (jamais
 * identiques entre re-renders). Couleur via `--color-separator`, opacité
 * `0.5`. Bord vide ~17 % de chaque côté pour le look organique.
 *
 * En `orientation='vertical'` : tombe sur une simple ligne 1px (le SVG
 * Bézier est conçu pour l'horizontal).
 *
 * Style horizontal piloté par l'attribut `data-separator-style` sur `<html>`
 * (réglage « Separator », cf. `applyPreferencesToDocument`) : `sketch` = trait
 * Bézier, `classic` = ligne droite, `none` = masqué. Même mécanisme que
 * `TextMark` pour le surlignage et le soulignement.
 *
 * **Pas SSR-safe** sans seed stable (paths générés au runtime).
 *
 * Migré du legacy `components/atoms/Separator/` — utilisé entre les groupes
 * de liste (EN COURS / À PRENDRE), dans le markdown Lexical, et autres
 * sections décoratives.
 */

import { type HTMLAttributes, useMemo } from 'react';
import { cn } from '@/lib/utils/cn';

export interface SeparatorProps extends HTMLAttributes<HTMLDivElement> {
  orientation?: 'horizontal' | 'vertical';
}

const rnd = (range: number): number => (Math.random() - 0.5) * range;

/** Deux quadratic Bézier ~17 → 83 dans un viewBox 0-100, sans symétrie. */
const generatePaths = (): readonly [string, string] => [
  `M ${String(17 + rnd(2))} ${String(4 + rnd(1.5))} Q ${String(50 + rnd(20))} ${String(1.5 + rnd(2))} ${String(83 + rnd(2))} ${String(4 + rnd(1.5))}`,
  `M ${String(17 + rnd(2))} ${String(6.5 + rnd(1))} Q ${String(50 + rnd(20))} ${String(4 + rnd(2))} ${String(83 + rnd(2))} ${String(6.5 + rnd(1))}`,
];

export function Separator({ orientation = 'horizontal', className, ...rest }: SeparatorProps) {
  const [path1, path2] = useMemo(generatePaths, []);

  if (orientation === 'vertical') {
    return (
      <div
        role="separator"
        aria-orientation="vertical"
        className={cn('bg-border h-full w-px shrink-0', className)}
        {...rest}
      />
    );
  }

  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      aria-hidden="true"
      className={cn('pointer-events-none w-full', className)}
      {...rest}
    >
      {/* Style `classic` : ligne droite, centrée dans la hauteur du trait Bézier. */}
      <div
        data-separator-line="classic"
        className="my-1 hidden h-px w-full bg-(--color-separator) opacity-50 [html[data-separator-style=classic]_&]:block"
      />
      <svg
        viewBox="0 0 100 10"
        preserveAspectRatio="none"
        className="block h-2 w-full overflow-visible opacity-50 [html[data-separator-style=classic]_&]:hidden [html[data-separator-style=none]_&]:hidden"
      >
        <path
          d={path1}
          vectorEffect="non-scaling-stroke"
          fill="none"
          stroke="var(--color-separator)"
          strokeWidth="1"
          strokeLinecap="round"
        />
        <path
          d={path2}
          vectorEffect="non-scaling-stroke"
          fill="none"
          stroke="var(--color-separator)"
          strokeWidth="1"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}
